import React, { useEffect, useState, useMemo } from "react";
import { db } from "../utils/firebase";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import {
  collection,
  where,
  getDocs,
  query,
  updateDoc,
  limit,
  deleteDoc,
  doc,
  orderBy,
  getDoc,
  writeBatch,
} from "firebase/firestore";
import { NavLink, NavDropdown } from "./index";
import SettingsModal from "../components/Modals/SettingsModal";
import {
  Banknote,
  BedDouble,
  Bell,
  GraduationCap,
  Settings,
  FileText,
  UserCircle,
  HelpCircle,
  Medal,
  Beaker,
  ClockIcon,
  Home,
  LayoutDashboard,
  LogOut,
  Menu,
  FileEdit,
  PieChart,
  Plus,
  Shield,
  Users,
  View,
  TrendingUp,
} from "lucide-react";
import { useAuth } from "../hooks/AuthenticationProvider";
import { SubscriptionGate } from "../utils/SubscriptionManager";

const Nav = () => {
  const {
    user,
    organizationId,
    organizationData,
    role,
    checkPermission,
    logout,
    isLoading,
    checkFeature,
  } = useAuth();

  const navigate = useNavigate();
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [phasesEnabled, setPhasesEnabled] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [menuHouse, setMenuHouse] = useState(false);
  const [visibleCount, setVisibleCount] = useState(5);
  const [showAllNotifications, setShowAllNotifications] = useState(false);
  const [menuAdmin, setMenuAdmin] = useState(false);
  const [activeSettingsTab, setActiveSettingsTab] = useState("appearance");
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationsVisible, setNotificationsVisible] = useState(false);
  const [selectedProfileUpdate, setSelectedProfileUpdate] = useState(null);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [selectedAccommodation, setSelectedAccommodation] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [selectedOvernight, setSelectedOvernight] = useState(null);
  const [overnightResponse, setOvernightResponse] = useState({
    status: "approved",
    note: "",
  });

  const canAccessOwner = checkPermission("accessOwnerDashboard");
  const canViewPayments = checkPermission("viewPayments");

  const permissions = useMemo(
    () => ({
      canAccessOwner,
      canViewPayments,
    }),
    [canAccessOwner, canViewPayments]
  );

  const isPlatformManagerAllowed =
    user?.email === "adam@reverencehouse.com" ||
    user?.email === "recoveryhouseplatform@gmail.com";

  const navItems = useMemo(() => {
    // This function filters items by both permission AND feature toggle
    const filterByPermissionAndFeature = (items) =>
      items.filter((item) => {
        // Check permission first
        const hasPermission = item.permission
          ? checkPermission(item.permission)
          : true;

        // Then check if the feature is enabled (if specified)
        const featureEnabled = item.feature ? checkFeature(item.feature) : true;

        // Special case for UA which has two related features
        if (item.featureCombo) {
          const { features, operator } = item.featureCombo;

          if (operator === "OR") {
            // If ANY feature is enabled, show the item
            return hasPermission && features.some((f) => checkFeature(f));
          } else if (operator === "AND") {
            // If ALL features are disabled, hide the item
            const allDisabled = features.every((f) => !checkFeature(f));
            return hasPermission && !allDisabled;
          }
        }

        return hasPermission && featureEnabled;
      });

    const houseManagementItems = [
      {
        title: "New Resident",
        link: "/intake",
        icon: <Plus size="18" />,
        permission: "canAddResidents",
      },
      {
        title: "Residents",
        link: "/residents",
        icon: <Users size="18" />,
        permission: "viewResidents",
      },
      {
        title: "Alumni",
        link: "/alumni",
        icon: <GraduationCap size="18" />,
        permission: "viewAlumni",
      },
      {
        title: "Beds",
        link: "/beds",
        icon: <BedDouble size="18" />,
        permission: "viewBeds",
        feature: "bedPlatform",
      },
      {
        title: "UA",
        link: "/ua",
        icon: <Beaker size="18" />,
        permission: "viewUa",
        featureCombo: {
          features: ["uaRecording", "uaScheduling"],
          operator: "OR",
        },
      },
      {
        title: "Sponsorship",
        link: "/sponsorship",
        icon: <Users size="18" />,
        permission: "viewSponsorship",
        feature: "sponsorship",
      },
      ...(phasesEnabled
        ? [
            {
              title: "Phases",
              link: "/phases",
              icon: <Medal size="18" />,
              permission: "viewPhases",
              feature: "phases",
            },
          ]
        : []),
    ];

    return {
      topLevel: filterByPermissionAndFeature([
        {
          title: "Dashboard",
          link: "/",
          icon: <LayoutDashboard size="20" />,
          permission: "viewDashboard",
        },
        {
          title: "Analytics",
          link: "/analytics",
          icon: <PieChart size="20" />,
          permission: "viewAnalytics",
          feature: "analytics",
        },
        {
          title: "Recovery Capital",
          link: "/recoverycapitaldata",
          icon: <TrendingUp size="20" />,
          permission: "viewRecoveryCapital",
          feature: "recoveryCapital",
        },
      ]),
      houseManagement: filterByPermissionAndFeature(houseManagementItems),
      administration: filterByPermissionAndFeature([
        {
          title: "Payments",
          link: "/payments",
          icon: <Banknote size="18" />,
          permission: "viewPayments",
          feature: "payments",
        },
        {
          title: "Applications",
          link: "/applications",
          icon: <FileText size="20" />,
          permission: "seeApplications",
          feature: "applications",
        },
        {
          title: "Forms",
          link: "/forms",
          icon: <FileEdit size="18" />,
          permission: "createForms",
          feature: "customForms",
        },
        ...(scheduleEnabled
          ? [
              {
                title: "Schedule",
                link: "/schedule",
                icon: <ClockIcon size="18" />,
                permission: "updateSchedule",
              },
            ]
          : []),
        {
          title: "Overview",
          link: "/owner",
          icon: <View size="18" />,
          permission: "accessOwnerDashboard",
          feature: "ownerDashboard",
        },
        ...(isPlatformManagerAllowed
          ? [
              {
                title: "Platform Manager",
                link: "/platform-manager",
                icon: <Users size="20" />,
              },
            ]
          : []),
      ]),
    };
  }, [
    checkPermission,
    checkFeature, // Add checkFeature as a dependency
    isPlatformManagerAllowed,
    scheduleEnabled,
    organizationData,
  ]);

  useEffect(() => {
    const checkFeatures = async () => {
      if (!organizationId) {
        console.log("No organizationId, waiting...");
        return;
      }

      try {
        console.log("Checking features for org:", organizationId);
        const houseSettingsRef = doc(
          db,
          `organizations/${organizationId}/houseSettings/initial`
        );
        const settingsSnap = await getDoc(houseSettingsRef);

        if (settingsSnap.exists()) {
          const settings = settingsSnap.data();
          const count = settings.paidResDashboardCount;

          // Check for both schedule and phases features
          setScheduleEnabled(count > 0);

          // You can use the same count or check for a different field
          // For example, if there's a specific field for phases:
          // setPhasesEnabled(settings.phasesEnabled === true);
          // Or use the same count threshold:
          setPhasesEnabled(count > 0);
        } else {
          console.log("Settings document does not exist");
          setScheduleEnabled(false);
          setPhasesEnabled(false);
        }
      } catch (error) {
        console.error("Error checking features:", error);
        setScheduleEnabled(false);
        setPhasesEnabled(false);
      }
    };

    checkFeatures();
  }, [organizationId, organizationData]);

  const handleMenuToggle = (menu) => {
    if (menu === "house") {
      setMenuHouse((prev) => !prev);
      setMenuAdmin(false);
    } else if (menu === "admin") {
      setMenuAdmin((prev) => !prev);
      setMenuHouse(false);
    }
  };

  const formatDate = (input) => {
    // First, determine if we're dealing with a notification object or a timestamp
    let timestamp;

    if (!input) return "No date available";

    // If input has a createdAt property, it's a notification object
    if (input.createdAt) {
      timestamp = input.createdAt;
    }
    // If input has a submittedAt property, use that
    else if (input.submittedAt) {
      timestamp = input.submittedAt;
    }
    // If input has a date property, use that
    else if (input.date) {
      timestamp = input.date;
    }
    // Otherwise, assume input is the timestamp itself
    else {
      timestamp = input;
    }

    // Now handle the timestamp based on its type
    if (timestamp?.toDate) {
      // Handle Firestore Timestamp
      return timestamp.toDate().toLocaleString();
    } else if (timestamp instanceof Date) {
      // Handle JavaScript Date object
      return timestamp.toLocaleString();
    } else if (typeof timestamp === "string") {
      // Handle string date
      try {
        return new Date(timestamp).toLocaleString();
      } catch (e) {
        console.error("Error parsing date string:", e);
        return "Invalid date";
      }
    } else if (typeof timestamp === "object" && timestamp.seconds) {
      // Handle Firestore timestamp format {seconds: number, nanoseconds: number}
      return new Date(timestamp.seconds * 1000).toLocaleString();
    }

    return "No date available";
  };

  useEffect(() => {
    // Guard: Ensure all required data is present before proceeding
    if (!organizationId || !user?.email) {
      console.log(
        "Waiting for required data before setting up notifications:",
        {
          organizationId,
          email: user?.email,
        }
      );
      return; // Do not proceed until data is available
    }

    console.log("Setting up notifications listeners");

    // Now fetch from the user's individual notifications subcollection
    const userNotificationsRef = collection(
      db,
      `organizations/${organizationId}/users/${user.email}/notifications`
    );

    // Only get recent and unread notifications (limit to 20)
    const notificationsQuery = query(
      userNotificationsRef,
      orderBy("createdAt", "desc"),
      limit(20)
    );

    // Get initial notifications with getDocs (one-time fetch instead of real-time)
    getDocs(notificationsQuery)
      .then((snapshot) => {
        const fetchedNotifications = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          source: "direct",
        }));
        console.log(
          `Fetched ${fetchedNotifications.length} initial notifications for user`
        );

        setNotifications(fetchedNotifications);
      })
      .catch((error) => {
        console.error("Error fetching notifications:", error);
      });

    // For overnight requests - use one-time fetch
    const overnightRef = collection(
      db,
      `organizations/${organizationId}/overnightRequests`
    );
    const overnightQuery = query(
      overnightRef,
      where("status", "==", "pending"),
      limit(10)
    );

    // One-time fetch for overnight requests
    getDocs(overnightQuery)
      .then((snapshot) => {
        const pendingRequests = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          type: "overnight",
          source: "overnight",
        }));

        // Add to notifications
        setNotifications((currentNotifications) => [
          ...currentNotifications,
          ...pendingRequests,
        ]);
      })
      .catch((error) => {
        console.error("Error fetching overnight requests:", error);
      });

    // For accommodation requests - use one-time fetch
    const accommodationRef = collection(
      db,
      `organizations/${organizationId}/accommodationRequests`
    );
    const accommodationQuery = query(
      accommodationRef,
      where("status", "==", "pending"),
      limit(10)
    );

    // One-time fetch for accommodation requests
    getDocs(accommodationQuery)
      .then((snapshot) => {
        const pendingAccommodations = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          type: "accommodation",
          source: "accommodation",
        }));

        // Add to notifications
        setNotifications((currentNotifications) => [
          ...currentNotifications,
          ...pendingAccommodations,
        ]);
      })
      .catch((error) => {
        console.error("Error fetching accommodation requests:", error);
      });

    return () => {
      console.log("No real-time listeners to clean up");
    };
  }, [organizationId, user?.email]);

  // FIXED FUNCTION - REPLACE THE ENTIRE FUNCTION
  const updateNotificationState = (newNotifications) => {
    setNotifications((prev) => {
      // First create an array of all unique notifications
      const allNotifications = [...prev, ...newNotifications].reduce(
        (acc, current) => {
          const exists = acc.find((item) => item.id === current.id);
          return exists ? acc : acc.concat(current);
        },
        []
      );

      // Then filter out notifications that have recipientIds but don't include current user
      const filteredNotifications = allNotifications.filter((notification) => {
        // If it has recipientIds field, check if current user is included
        if (
          notification.recipientIds &&
          Array.isArray(notification.recipientIds)
        ) {
          return notification.recipientIds.includes(user.email);
        }
        // Keep all other notifications
        return true;
      });

      // Sort by date
      return filteredNotifications.sort((a, b) => {
        const dateA = a.submittedAt?.toDate?.() || new Date(a.date);
        const dateB = b.submittedAt?.toDate?.() || new Date(b.date);
        return dateB - dateA;
      });
    });
  };

  useEffect(() => {
    const unreadCount = notifications.filter(
      (notification) => !notification.read
    ).length;
    setUnreadNotificationCount(unreadCount);
  }, [notifications]);

  const handleOvernightResponse = async () => {
    if (!selectedOvernight) return;

    try {
      const requestRef = doc(
        db,
        `organizations/${organizationId}/overnightRequests`,
        selectedOvernight.id
      );

      // Update the overnight request with the response
      await updateDoc(requestRef, {
        status: overnightResponse.status,
        reviewNotes: overnightResponse.note,
        reviewedBy: {
          uid: user.uid,
          name: `${user.firstName} ${user.lastName}`,
          email: user.email,
        },
        reviewedAt: new Date(),
      });

      // Remove from notifications list
      setNotifications((prev) =>
        prev.filter((notif) => notif.id !== selectedOvernight.id)
      );

      // Send notifications to all non-resident users
      await sendOvernightResponseNotifications();

      setSelectedOvernight(null);
      setOvernightResponse({ status: "approved", note: "" });
    } catch (error) {
      console.error("Error updating overnight request:", error);
    }
  };

  const parseDateAsLocal = (dateStr) => {
    const [year, month, day] = dateStr.split("-");
    return new Date(Number(year), Number(month) - 1, Number(day));
  };

  // Function to send notifications to all non-resident users
  const sendOvernightResponseNotifications = async () => {
    try {
      // First, get all users from the organization who are not residents
      const usersRef = collection(db, `organizations/${organizationId}/users`);
      const usersSnapshot = await getDocs(usersRef);

      // Filter users who are not residents
      const nonResidentUsers = usersSnapshot.docs
        .filter((doc) => {
          const userData = doc.data();
          return userData.role !== "Resident";
        })
        .map((doc) => ({
          email: doc.id,
          ...doc.data(),
        }));

      console.log(
        `Found ${nonResidentUsers.length} non-resident users to notify`
      );

      // Create notification content that matches existing structure
      const statusText =
        overnightResponse.status === "approved" ? "Approved" : "Denied";
      const subject = `Overnight ${statusText}`;
      const noteContent = `${
        selectedOvernight.residentName
      }'s overnight request for ${parseDateAsLocal(
        selectedOvernight.startDate
      ).toLocaleDateString()} - ${parseDateAsLocal(
        selectedOvernight.endDate
      ).toLocaleDateString()} has been ${overnightResponse.status}.${
        overnightResponse.note ? " Note: " + overnightResponse.note : ""
      }`;

      // Batch write to add notifications to each user's subcollection
      const batch = writeBatch(db);

      for (const staffUser of nonResidentUsers) {
        // Skip the current user who made the decision
        if (staffUser.email === user.email) continue;

        const notificationRef = doc(
          collection(
            db,
            `organizations/${organizationId}/users/${staffUser.email}/notifications`
          )
        );

        // Use the existing notification structure
        batch.set(notificationRef, {
          subject: subject,
          note: noteContent,
          date: new Date(),
          createdAt: new Date(),
          isRead: false,
          forceEmail: false,
          sourceType: "overnight_response",
          residentId: selectedOvernight.residentId,
        });
      }

      // Commit all notifications at once
      await batch.commit();
      console.log(
        `Sent overnight response notifications to ${
          nonResidentUsers.length - 1
        } users`
      );
    } catch (error) {
      console.error("Error sending overnight response notifications:", error);
    }
  };

  const handleLogout = async () => {
    try {
      // Clear local nav state first
      setNavOpen(false);
      setMenuHouse(false);
      setMenuAdmin(false);
      setDropdownVisible(false);
      setNotificationsVisible(false);

      // Call the AuthProvider's logout
      await logout();
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  const handleDropdownToggle = () => {
    setDropdownVisible(!dropdownVisible);
  };

  const handleNotificationsToggle = () => {
    setNotificationsVisible(!notificationsVisible);
    setVisibleCount(5); // Reset count when opening modal
    setShowAllNotifications(false);
  };

  const handleDeleteAllNotifications = async () => {
    try {
      const batch = writeBatch(db);
      const notificationsRef = collection(
        db,
        `organizations/${organizationId}/users/${user.email}/notifications`
      );
      const snapshot = await getDocs(notificationsRef);

      snapshot.docs.forEach((doc) => {
        batch.delete(doc.ref);
      });

      await batch.commit();
      setNotifications((prev) =>
        prev.filter((n) => n.type === "overnight" || n.type === "accommodation")
      );
    } catch (error) {
      console.error("Error deleting notifications:", error);
    }
  };

  const handleNotificationClick = async (notification) => {
    if (!notification) return;

    try {
      if (notification.type === "overnight") {
        setSelectedOvernight(notification);
      } else if (notification.type === "accommodation") {
        setSelectedAccommodation(notification);
      } else if (notification.sourceType === "profileUpdate") {
        // Always fetch and show the profile update request, regardless of read status
        const requestRef = doc(
          db,
          `organizations/${organizationId}/profileUpdates`,
          notification.updateRequestId
        );
        const requestDoc = await getDoc(requestRef);

        if (requestDoc.exists()) {
          // Get the profile update data before setting state
          const profileUpdateData = { id: requestDoc.id, ...requestDoc.data() };

          // Log the data BEFORE setting state to ensure we have access to it
          console.log(
            "PROFILE UPDATE REQUEST - FULL OBJECT:",
            profileUpdateData
          );
          console.log("PREVIOUS FIELDS:", profileUpdateData.previousFields);
          console.log("UPDATED FIELDS:", profileUpdateData.updatedFields);

          // Now set the state with the data we've already verified
          setSelectedProfileUpdate(profileUpdateData);

          // Mark as read but still show the modal
          if (!notification.read) {
            const notificationRef = doc(
              db,
              `organizations/${organizationId}/users/${user.email}/notifications`,
              notification.id
            );
            await updateDoc(notificationRef, { read: true });

            setNotifications((prev) =>
              prev.map((n) =>
                n.id === notification.id ? { ...n, read: true } : n
              )
            );
          }
        } else {
          // If the request has been approved/denied already, remove this notification
          const notificationRef = doc(
            db,
            `organizations/${organizationId}/users/${user.email}/notifications`,
            notification.id
          );
          await deleteDoc(notificationRef);

          setNotifications((prev) =>
            prev.filter((n) => n.id !== notification.id)
          );

          alert("This profile update request has already been processed.");
          return;
        }
      } else if (
        notification.type === "application" ||
        notification.sourceType === "application" ||
        (notification.subject &&
          notification.subject.toLowerCase().includes("application")) ||
        (notification.title &&
          notification.title.toLowerCase().includes("application")) ||
        (notification.message &&
          notification.message.toLowerCase().includes("application"))
      ) {
        navigate("/applications");
        setNotificationsVisible(false);

        // Mark as read
        const notificationRef = doc(
          db,
          `organizations/${organizationId}/users/${user.email}/notifications`,
          notification.id
        );
        await updateDoc(notificationRef, { read: true });

        setNotifications((prev) =>
          prev.map((n) => (n.id === notification.id ? { ...n, read: true } : n))
        );
      } else {
        if (notification.type === "note" || notification.content) {
          setSelectedNotification(notification);
        }

        // Mark as read
        const notificationRef = doc(
          db,
          `organizations/${organizationId}/users/${user.email}/notifications`,
          notification.id
        );
        await updateDoc(notificationRef, { read: true });

        setNotifications((prev) =>
          prev.map((n) => (n.id === notification.id ? { ...n, read: true } : n))
        );
      }
    } catch (error) {
      console.error("Error updating notification:", error);
      alert("Error processing notification. Please try again.");
    }
  };

  const handleAccommodationAcknowledge = async () => {
    if (!selectedAccommodation) return;

    try {
      const requestRef = doc(
        db,
        `organizations/${organizationId}/accommodationRequests`,
        selectedAccommodation.id
      );
      await updateDoc(requestRef, {
        status: "acknowledged",
        reviewedBy: {
          uid: user.uid,
          name: `${user.firstName} ${user.lastName}`,
          email: user.email,
        },
        reviewedAt: new Date(),
      });

      // Remove from notifications list
      setNotifications((prev) =>
        prev.filter((notif) => notif.id !== selectedAccommodation.id)
      );

      setSelectedAccommodation(null);
    } catch (error) {
      console.error("Error acknowledging accommodation request:", error);
    }
  };

  const colors = {
    border: "border-slate-800/30",
    background: "bg-[#0A0F1E]",
    brand: "bg-[#0A0F1E]",
  };

  if (isLoading) {
    console.log("Auth state is still loading in Nav.jsx.");
    return <div>Loading...</div>;
  }

  return (
    <aside>
      <div
        className={`${
          navOpen ? "block" : "hidden"
        } fixed inset-0 left-0 bg-slate-900/50 backdrop-blur-sm z-10`}
        onClick={() => setNavOpen(false)}
      />
      <div
        className={`nav-shadow fixed left-0 top-0 right-0 z-20 h-14 py-3 px-4 border-b 
          ${colors.border} bg-gradient-to-b from-slate-900/95 to-slate-900/80 backdrop-blur-xl`}
      >
        {user && (
          <div className="flex flex-row items-center justify-between">
            <div className="w-16 xl:w-64">
              <button
                onClick={() => setNavOpen((prev) => !prev)}
                className="xl:hidden"
              >
                <Menu size={32} />
              </button>
            </div>
            <div className="flex-1 text-center">
              {!isLoading && (
                <h2 className="hidden xl:block text-xl">
                  Hello,{" "}
                  <span className="font-medium">
                    {user?.firstName ||
                      user?.displayName?.split(" ")[0] ||
                      "User"}
                  </span>
                  .
                </h2>
              )}
            </div>

            <div className="order-2 justify-self-end gap-4">
              <div className="relative text-left">
                <button
                  className="flex items-center justify-center rounded-full"
                  onClick={handleDropdownToggle}
                >
                  <UserCircle
                    size={32}
                    className={
                      dropdownVisible ? "fill-white" : "hover:fill-neutral-300"
                    }
                  />
                </button>
                {unreadNotificationCount > 0 && (
                  <div className="absolute -top-1 -right-1 bg-red-500 rounded-full w-5 h-5 flex items-center justify-center">
                    <span className="text-xs font-medium text-white">
                      {unreadNotificationCount}
                    </span>
                  </div>
                )}
                {dropdownVisible && (
                  <div className="absolute right-0 mt-2 w-64 rounded-xl shadow-xl bg-slate-950/95 border border-slate-800">
                    <div className="p-2 space-y-1">
                      {checkPermission("viewSettings") && (
                        <button
                          onClick={() => {
                            setShowSettings(true);
                            setDropdownVisible(false);
                            setActiveSettingsTab(
                              location.state?.openSettingsWithTab ||
                                "appearance"
                            );
                          }}
                          className="w-full p-3 rounded-lg hover:bg-slate-800/70 transition-colors cursor-pointer flex items-center gap-3"
                        >
                          <Settings size={18} className="text-slate-300" />
                          <span className="text-white">Settings</span>
                        </button>
                      )}

                      <button
                        onClick={handleNotificationsToggle}
                        className="w-full p-3 rounded-lg hover:bg-slate-800/70 transition-colors cursor-pointer flex items-center gap-3 relative"
                      >
                        <Bell size={18} className="text-slate-300" />
                        <span className="text-white">Notifications</span>
                        {unreadNotificationCount > 0 && (
                          <div className="absolute top-1/2 -translate-y-1/2 right-3 w-2 h-2 bg-red-500 rounded-full" />
                        )}
                      </button>
                      <NavLink
                        title="Help"
                        link="/help"
                        icon={
                          <HelpCircle size={18} className="text-slate-300" />
                        }
                        className="w-full p-3 rounded-lg hover:bg-slate-800/70 transition-colors cursor-pointer flex items-center gap-3"
                      />
                      <button
                        onClick={handleLogout}
                        className="w-full p-3 rounded-lg hover:bg-slate-800/70 transition-colors cursor-pointer flex items-center gap-3"
                      >
                        <LogOut size={18} className="text-slate-300" />
                        <span className="text-white">Logout</span>
                      </button>
                    </div>
                  </div>
                )}

                {showSettings && (
                  <SettingsModal onClose={() => setShowSettings(false)} />
                )}
                {notificationsVisible && (
                  <div
                    className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/80 backdrop-blur-sm"
                    onClick={(e) => {
                      if (e.target === e.currentTarget)
                        setNotificationsVisible(false);
                    }}
                  >
                    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" />
                    <div className="fixed inset-0 z-[999] h-screen w-screen grid place-items-center p-4">
                      <div className="relative w-96 bg-slate-950/95 rounded-xl shadow-2xl border border-slate-800">
                        <div className="flex items-center justify-between p-6 border-b border-slate-800">
                          <p className="text-2xl text-white font-semibold">
                            Notifications
                          </p>
                          <button
                            className="text-slate-400 hover:text-slate-200 transition-colors"
                            onClick={() => setNotificationsVisible(false)}
                          >
                            Close
                          </button>
                        </div>
                        <div className="p-6 overflow-y-auto max-h-[60vh] space-y-4">
                          {(showAllNotifications
                            ? notifications
                            : notifications.filter((n) => !n.isRead).slice(0, 5)
                          ).map((notification) => (
                            <div
                              key={notification.id}
                              className="p-4 bg-slate-800/50 rounded-lg hover:bg-slate-800/70 transition-colors cursor-pointer"
                              onClick={() =>
                                handleNotificationClick(notification)
                              }
                            >
                              <p className="text-lg font-medium text-white">
                                {notification.subject ||
                                  (notification.type === "overnight"
                                    ? "Overnight Request"
                                    : notification.type === "accommodation"
                                    ? "Reasonable Accommodation Request"
                                    : "Notification")}
                              </p>
                              <p className="text-sm text-slate-300">
                                {notification.note ||
                                  (notification.type === "overnight"
                                    ? notification.contingencyPlan
                                    : "")}
                              </p>
                              <p className="text-xs text-slate-500">
                                {formatDate(notification)}
                              </p>
                            </div>
                          ))}
                        </div>
                        <div className="flex justify-between items-center p-6 border-t border-slate-800">
                          {!showAllNotifications && (
                            <button
                              onClick={() => setShowAllNotifications(true)}
                              className="text-sm text-blue-500 hover:text-blue-400"
                            >
                              Show More
                            </button>
                          )}
                          <button
                            onClick={handleDeleteAllNotifications}
                            className="text-sm text-red-500 hover:text-red-400 ml-auto"
                          >
                            Delete All Notifications
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {selectedAccommodation && (
                  <div
                    className="fixed inset-0 z-[9999999999] flex items-center justify-center bg-black/80 backdrop-blur-sm"
                    onClick={(e) => {
                      if (e.target === e.currentTarget)
                        setSelectedAccommodation(null);
                    }}
                  >
                    <div
                      className="absolute top-1/4 w-full max-w-md bg-slate-950/95 rounded-xl border border-slate-800 shadow-xl p-6"
                      style={{ top: "20%" }}
                    >
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-xl font-semibold text-white">
                          Accommodation Request
                        </h3>
                        <button
                          onClick={() => setSelectedAccommodation(null)}
                          className="text-slate-400 hover:text-slate-200"
                        >
                          Close
                        </button>
                      </div>
                      <div className="space-y-4">
                        <div>
                          <p className="text-sm text-slate-400">Resident</p>
                          <p className="text-white">
                            {selectedAccommodation.residentName}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-400">
                            Accommodation Type
                          </p>
                          <p className="text-white">
                            {selectedAccommodation.accommodationType}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-400">Description</p>
                          <p className="text-white">
                            {selectedAccommodation.description}
                          </p>
                        </div>
                        <button
                          onClick={handleAccommodationAcknowledge}
                          className="w-full py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
                        >
                          Acknowledge Request
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                {selectedOvernight && (
                  <div
                    className="fixed inset-0 z-[9999999999] flex items-center justify-center bg-black/80 backdrop-blur-sm"
                    onClick={(e) => {
                      if (e.target === e.currentTarget)
                        setSelectedOvernight(null);
                    }}
                  >
                    <div
                      className="absolute top-1/4 w-full max-w-md bg-slate-950/95 rounded-xl border border-slate-800 shadow-xl p-6"
                      style={{ top: "20%" }}
                    >
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-xl font-semibold text-white">
                          Overnight Request
                        </h3>
                        <button
                          onClick={() => setSelectedOvernight(null)}
                          className="text-slate-400 hover:text-slate-200"
                        >
                          Close
                        </button>
                      </div>
                      <div className="space-y-4">
                        <div>
                          <p className="text-sm text-slate-400">Resident</p>
                          <p className="text-white">
                            {selectedOvernight.residentName}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-400">Dates</p>
                          <p className="text-white">
                            {parseDateAsLocal(
                              selectedOvernight.startDate
                            ).toLocaleDateString()}{" "}
                            -{" "}
                            {parseDateAsLocal(
                              selectedOvernight.endDate
                            ).toLocaleDateString()}
                          </p>
                        </div>

                        <div>
                          <p className="text-sm text-slate-400">Location</p>
                          <p className="text-white">
                            {selectedOvernight.location}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-400">
                            Contingency Plan
                          </p>
                          <p className="text-white">
                            {selectedOvernight.contingencyPlan}
                          </p>
                        </div>
                        <div className="space-y-2">
                          <label className="block text-sm text-slate-400">
                            Response
                          </label>
                          <select
                            value={overnightResponse.status}
                            onChange={(e) =>
                              setOvernightResponse((prev) => ({
                                ...prev,
                                status: e.target.value,
                              }))
                            }
                            className="w-full bg-slate-800/50 rounded-lg px-4 py-2 text-white border border-slate-600"
                          >
                            <option value="approved">Confirmed</option>
                            <option value="denied">Denied</option>
                          </select>
                        </div>
                        <div className="space-y-2">
                          <label className="block text-sm text-slate-400">
                            Note
                          </label>
                          <textarea
                            value={overnightResponse.note}
                            onChange={(e) =>
                              setOvernightResponse((prev) => ({
                                ...prev,
                                note: e.target.value,
                              }))
                            }
                            placeholder="Add a note to the resident..."
                            className="w-full bg-slate-800/50 rounded-lg px-4 py-2 text-white border border-slate-600 min-h-[100px]"
                          />
                        </div>
                        <button
                          onClick={handleOvernightResponse}
                          className="w-full py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
                        >
                          Submit Response
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {selectedProfileUpdate && (
          <div
            className="fixed inset-0 z-[9999999999] flex items-center justify-center bg-black/80 backdrop-blur-sm"
            onClick={(e) => {
              if (e.target === e.currentTarget) setSelectedProfileUpdate(null);
            }}
          >
            <div className="absolute top-1/4 w-full max-w-2xl bg-slate-950/95 rounded-xl border border-slate-800 shadow-xl p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-white">
                  Profile Update Request - {selectedProfileUpdate.residentName}
                </h3>
                <button
                  onClick={() => setSelectedProfileUpdate(null)}
                  className="text-slate-400 hover:text-slate-200"
                >
                  Close
                </button>
              </div>

              <div className="space-y-4 max-h-[60vh] overflow-y-auto">
                {/* Display changes */}
                {Object.entries(selectedProfileUpdate.updatedFields).map(
                  ([key, newValue]) => {
                    const oldValue = selectedProfileUpdate.previousFields[key];

                    // Better deep comparison for actual changes
                    const hasChanged = (() => {
                      // Simple comparison for primitive values
                      if (typeof newValue !== "object" || newValue === null) {
                        return oldValue !== newValue;
                      }

                      // For arrays of objects
                      if (Array.isArray(newValue) && Array.isArray(oldValue)) {
                        if (key === "medications") {
                          // Compare medications by name and dose
                          if (newValue.length !== oldValue.length) return true;

                          // Check if any medication details changed
                          return newValue.some(
                            (med, i) =>
                              !oldValue[i] ||
                              med.name !== oldValue[i].name ||
                              med.dose !== oldValue[i].dose
                          );
                        }

                        if (key === "employmentHistory") {
                          // Compare employment history
                          if (newValue.length !== oldValue.length) return true;

                          // Check if any job details changed
                          return newValue.some(
                            (job, i) =>
                              !oldValue[i] ||
                              job.employer !== oldValue[i].employer ||
                              job.status !== oldValue[i].status ||
                              job.date !== oldValue[i].date
                          );
                        }

                        if (key === "drugOfChoice") {
                          // Compare arrays of strings
                          if (newValue.length !== oldValue.length) return true;
                          return !newValue.every(
                            (val, i) => val === oldValue[i]
                          );
                        }

                        // For other arrays, convert to string and compare
                        return (
                          JSON.stringify(oldValue) !== JSON.stringify(newValue)
                        );
                      }

                      // For other objects, use JSON stringify as fallback
                      return (
                        JSON.stringify(oldValue) !== JSON.stringify(newValue)
                      );
                    })();

                    // Only show fields that have actually changed
                    if (hasChanged) {
                      return (
                        <div key={key} className="space-y-2">
                          <p className="text-sm text-slate-400 font-medium">
                            {key === "poFirstName"
                              ? "Probation/Parole First Name"
                              : key === "poLastName"
                              ? "Probation/Parole Last Name"
                              : key === "ecFirstName"
                              ? "Emergency Contact First Name"
                              : key === "ecLastName"
                              ? "Emergency Contact Last Name"
                              : key === "ecPhoneNumber"
                              ? "Emergency Contact Phone"
                              : key === "ecRelationship"
                              ? "Emergency Contact Relationship"
                              : key === "dob"
                              ? "Date of Birth"
                              : key === "isMAT"
                              ? "Medication Assisted Treatment"
                              : key.replace(/([A-Z])/g, " $1").trim()}
                          </p>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="bg-red-500/10 p-3 rounded-lg">
                              <p className="text-xs text-red-400 mb-1">
                                Previous Value
                              </p>
                              <div className="text-white">
                                {(() => {
                                  // Special handling based on field type
                                  if (
                                    key === "medications" &&
                                    Array.isArray(oldValue)
                                  ) {
                                    return (
                                      <div className="space-y-1 text-sm">
                                        {oldValue.length === 0
                                          ? "(No medications)"
                                          : oldValue.map((med, idx) => (
                                              <div key={idx} className="p-1">
                                                {med.name}
                                                {med.dose
                                                  ? ` - ${med.dose}`
                                                  : ""}
                                              </div>
                                            ))}
                                      </div>
                                    );
                                  } else if (
                                    key === "employmentHistory" &&
                                    Array.isArray(oldValue)
                                  ) {
                                    return (
                                      <div className="space-y-1 text-sm">
                                        {oldValue.length === 0
                                          ? "(No employment history)"
                                          : oldValue.map((job, idx) => (
                                              <div key={idx} className="p-1">
                                                {job.employer} - {job.status} (
                                                {job.date})
                                              </div>
                                            ))}
                                      </div>
                                    );
                                  } else if (key === "isMAT") {
                                    return oldValue ? "Yes" : "No";
                                  } else if (
                                    key === "drugOfChoice" &&
                                    Array.isArray(oldValue)
                                  ) {
                                    return oldValue.length === 0
                                      ? "(None specified)"
                                      : oldValue.join(", ");
                                  } else if (Array.isArray(oldValue)) {
                                    return oldValue.length === 0
                                      ? "(Empty array)"
                                      : oldValue.join(", ");
                                  } else if (
                                    typeof oldValue === "object" &&
                                    oldValue !== null
                                  ) {
                                    return (
                                      <pre className="whitespace-pre-wrap text-xs overflow-auto max-h-40">
                                        {JSON.stringify(oldValue, null, 2)}
                                      </pre>
                                    );
                                  } else {
                                    return oldValue || "(empty)";
                                  }
                                })()}
                              </div>
                            </div>
                            <div className="bg-green-500/10 p-3 rounded-lg">
                              <p className="text-xs text-green-400 mb-1">
                                New Value
                              </p>
                              <div className="text-white">
                                {(() => {
                                  // Special handling based on field type
                                  if (
                                    key === "medications" &&
                                    Array.isArray(newValue)
                                  ) {
                                    return (
                                      <div className="space-y-1 text-sm">
                                        {newValue.length === 0
                                          ? "(No medications)"
                                          : newValue.map((med, idx) => (
                                              <div key={idx} className="p-1">
                                                {med.name}
                                                {med.dose
                                                  ? ` - ${med.dose}`
                                                  : ""}
                                              </div>
                                            ))}
                                      </div>
                                    );
                                  } else if (
                                    key === "employmentHistory" &&
                                    Array.isArray(newValue)
                                  ) {
                                    return (
                                      <div className="space-y-1 text-sm">
                                        {newValue.length === 0
                                          ? "(No employment history)"
                                          : newValue.map((job, idx) => (
                                              <div key={idx} className="p-1">
                                                {job.employer} - {job.status} (
                                                {job.date})
                                              </div>
                                            ))}
                                      </div>
                                    );
                                  } else if (key === "isMAT") {
                                    return newValue ? "Yes" : "No";
                                  } else if (
                                    key === "drugOfChoice" &&
                                    Array.isArray(newValue)
                                  ) {
                                    return newValue.length === 0
                                      ? "(None specified)"
                                      : newValue.join(", ");
                                  } else if (Array.isArray(newValue)) {
                                    return newValue.length === 0
                                      ? "(Empty array)"
                                      : newValue.join(", ");
                                  } else if (
                                    typeof newValue === "object" &&
                                    newValue !== null
                                  ) {
                                    return (
                                      <pre className="whitespace-pre-wrap text-xs overflow-auto max-h-40">
                                        {JSON.stringify(newValue, null, 2)}
                                      </pre>
                                    );
                                  } else {
                                    return newValue || "(empty)";
                                  }
                                })()}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }
                )}
              </div>

              <div className="flex justify-end gap-4 mt-6 pt-4 border-t border-slate-700">
                <button
                  onClick={async () => {
                    try {
                      // Delete the request document
                      const requestRef = doc(
                        db,
                        `organizations/${organizationId}/profileUpdates`,
                        selectedProfileUpdate.id
                      );
                      await deleteDoc(requestRef);

                      // Remove the notification
                      const notificationsRef = collection(
                        db,
                        `organizations/${organizationId}/users/${user.email}/notifications`
                      );
                      const notificationsSnapshot = await getDocs(
                        query(
                          notificationsRef,
                          where(
                            "updateRequestId",
                            "==",
                            selectedProfileUpdate.id
                          )
                        )
                      );

                      notificationsSnapshot.docs.forEach(async (doc) => {
                        await deleteDoc(doc.ref);
                      });

                      // Update local state
                      setNotifications((prev) =>
                        prev.filter(
                          (notif) =>
                            notif.updateRequestId !== selectedProfileUpdate.id
                        )
                      );

                      setSelectedProfileUpdate(null);
                      alert("Profile update request denied.");
                    } catch (error) {
                      console.error("Error rejecting profile update:", error);
                      alert(
                        "Failed to reject profile update. Please try again."
                      );
                    }
                  }}
                  className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                >
                  Deny
                </button>
                <button
                  onClick={async () => {
                    try {
                      // Update the resident's profile
                      const residentRef = doc(
                        db,
                        `organizations/${organizationId}/residents`,
                        selectedProfileUpdate.residentId
                      );
                      await updateDoc(
                        residentRef,
                        selectedProfileUpdate.updatedFields
                      );

                      // Delete the request document
                      const requestRef = doc(
                        db,
                        `organizations/${organizationId}/profileUpdates`,
                        selectedProfileUpdate.id
                      );
                      await deleteDoc(requestRef);

                      // Remove the notification
                      const notificationsRef = collection(
                        db,
                        `organizations/${organizationId}/users/${user.email}/notifications`
                      );
                      const notificationsSnapshot = await getDocs(
                        query(
                          notificationsRef,
                          where(
                            "updateRequestId",
                            "==",
                            selectedProfileUpdate.id
                          )
                        )
                      );

                      notificationsSnapshot.docs.forEach(async (doc) => {
                        await deleteDoc(doc.ref);
                      });

                      // Update local state
                      setNotifications((prev) =>
                        prev.filter(
                          (notif) =>
                            notif.updateRequestId !== selectedProfileUpdate.id
                        )
                      );

                      setSelectedProfileUpdate(null);
                      alert("Profile update approved successfully!");
                    } catch (error) {
                      console.error("Error approving profile update:", error);
                      alert(
                        "Failed to approve profile update. Please try again."
                      );
                    }
                  }}
                  className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
                >
                  Approve
                </button>
              </div>
            </div>
          </div>
        )}

        {selectedNotification && (
          <div
            className="fixed inset-0 z-[9999999999] flex items-center justify-center bg-black/80 backdrop-blur-sm"
            onClick={(e) => {
              if (e.target === e.currentTarget) setSelectedNotification(null);
            }}
          >
            <div className="absolute top-1/4 w-full max-w-md bg-slate-950/95 rounded-xl border border-slate-800 shadow-xl p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-white">
                  {selectedNotification.title || "Notification"}
                </h3>
                <button
                  onClick={() => setSelectedNotification(null)}
                  className="text-slate-400 hover:text-slate-200"
                >
                  Close
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-slate-400">Subject</p>
                  <p className="text-white">
                    {selectedNotification.message || ""}
                  </p>
                </div>
                {selectedNotification.content && (
                  <div>
                    <p className="text-sm text-slate-400">Note</p>
                    <p className="text-white whitespace-pre-wrap">
                      {selectedNotification.content}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-slate-400">Date</p>
                  <p className="text-white">
                    {formatDate(selectedNotification)}
                  </p>
                </div>

                {selectedNotification.residentId &&
                  checkPermission("viewResidents") && (
                    <button
                      onClick={() => {
                        navigate(`/profile/${selectedNotification.residentId}`); // Changed from /resident/ to /profile/
                        setSelectedNotification(null);
                        setNotificationsVisible(false);
                      }}
                      className="w-full py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
                    >
                      View Resident Profile
                    </button>
                  )}
              </div>
            </div>
          </div>
        )}
      </div>

      <nav
        className={`${
          navOpen ? "block" : "hidden xl:flex"
        } fixed left-0 top-0 h-[calc(100%-56px)] w-64 mt-14 px-4 py-6 z-40 
        flex flex-col border-r border-slate-800/30 bg-slate-900/95 
        backdrop-blur-xl shadow-lg`}
      >
        <SubscriptionGate>
          <div className="space-y-3 text-base font-medium">
            {navItems.topLevel.map((item) => (
              <NavLink
                key={item.title}
                title={item.title}
                link={item.link}
                icon={item.icon}
              />
            ))}
            <NavDropdown
              title="House Management"
              icon={<Home size={20} />}
              onClick={() => handleMenuToggle("house")}
              popup={menuHouse}
            >
              {navItems.houseManagement.map((item) => (
                <NavLink
                  key={item.title}
                  title={item.title}
                  link={item.link}
                  icon={item.icon}
                />
              ))}
            </NavDropdown>
            {navItems.administration.length > 0 && (
              <NavDropdown
                title="Administration"
                icon={<Shield size={20} />}
                onClick={() => handleMenuToggle("admin")}
                popup={menuAdmin}
              >
                {navItems.administration.map((item) => (
                  <NavLink
                    key={item.title}
                    title={item.title}
                    link={item.link}
                    icon={item.icon}
                  />
                ))}
              </NavDropdown>
            )}
          </div>
        </SubscriptionGate>
      </nav>
    </aside>
  );
};

export default Nav;
